/**
 * API Client
 *
 * Centralized HTTP client with interceptors for authentication
 * and error handling
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios'
import { env } from '@/config/env'

// =============================================================================
// Types
// =============================================================================

export interface ApiError {
  statusCode: number
  error: string
  message: string
}

export interface ApiResponse<T> {
  data: T
  message?: string
}

/**
 * Extract error message from various error types
 * Handles AxiosError, Error, and unknown types
 */
export function getErrorMessage(error: unknown, fallback = 'Error desconocido'): string {
  // Check if it's an AxiosError with response data
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as ApiError | undefined
    if (responseData?.message) {
      return responseData.message
    }
    if (error.code === 'ECONNABORTED') {
      return 'La carga se interrumpió porque tardó más de lo permitido. Con estudios grandes suele ser la conexión: vuelva a intentarlo, o deje los archivos en la carpeta vigilada.'
    }
    if (error.code === 'ERR_NETWORK') {
      return 'No se pudo conectar con el servidor. Compruebe la conexión y vuelva a intentarlo.'
    }
    // Fall back to axios error message
    return error.message || fallback
  }

  // Check if it's a standard Error
  if (error instanceof Error) {
    return error.message
  }

  // Unknown error type
  return fallback
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

// =============================================================================
// Token Storage - Staff/Admin
// =============================================================================

const TOKEN_KEY = 'jh_lumina_access_token'
const REFRESH_TOKEN_KEY = 'jh_lumina_refresh_token'

export const tokenStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY)
  },

  setAccessToken: (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token)
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token)
  },

  clearTokens: (): void => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    tokenStorage.setAccessToken(accessToken)
    tokenStorage.setRefreshToken(refreshToken)
  },
}

// =============================================================================
// Token Storage - Patient (Completely separate from staff)
// =============================================================================

const PATIENT_TOKEN_KEY = 'jh_lumina_patient_token'
const PATIENT_INFO_KEY = 'jh_lumina_patient_info'

export const patientTokenStorage = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(PATIENT_TOKEN_KEY)
  },

  setAccessToken: (token: string): void => {
    localStorage.setItem(PATIENT_TOKEN_KEY, token)
  },

  clearTokens: (): void => {
    localStorage.removeItem(PATIENT_TOKEN_KEY)
    localStorage.removeItem(PATIENT_INFO_KEY)
  },

  getPatientInfo: (): unknown => {
    const info = localStorage.getItem(PATIENT_INFO_KEY)
    return info ? JSON.parse(info) : null
  },

  setPatientInfo: (info: unknown): void => {
    localStorage.setItem(PATIENT_INFO_KEY, JSON.stringify(info))
  },
}

// =============================================================================
// API Client Factory
// =============================================================================

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token!)
    }
  })
  failedQueue = []
}

const UN_ARCHIVO_DENTRO = 100_000

const esContenidoDeArchivo = (valor: unknown): boolean =>
  typeof valor === 'string' && valor.length > UN_ARCHIVO_DENTRO

/**
 * Si la peticion lleva archivos dentro.
 *
 * Se mira sin serializar el cuerpo: con una serie de tomografia son cientos de
 * megabytes y medirlos costaria tanto como enviarlos.
 */
const llevaArchivos = (datos: unknown): boolean => {
  if (!datos || typeof datos !== 'object') return false
  if (typeof FormData !== 'undefined' && datos instanceof FormData) return true
  if (typeof Blob !== 'undefined' && datos instanceof Blob) return true
  if (datos instanceof ArrayBuffer) return true

  for (const valor of Object.values(datos as Record<string, unknown>)) {
    if (esContenidoDeArchivo(valor)) return true
    if (!Array.isArray(valor)) continue
    for (const elemento of valor) {
      if (esContenidoDeArchivo(elemento)) return true
      if (elemento && typeof elemento === 'object') {
        if (Object.values(elemento as Record<string, unknown>).some(esContenidoDeArchivo)) return true
      }
    }
  }
  return false
}

const darleTiempoSiLlevaArchivos = (config: InternalAxiosRequestConfig): void => {
  if (config.timeout === env.API_TIMEOUT_SUBIDA) return
  if (llevaArchivos(config.data)) config.timeout = env.API_TIMEOUT_SUBIDA
}

/** Si el token de acceso ya no sirve, mirando su propia fecha. */
const estaCaducado = (accessToken: string): boolean => {
  try {
    const carga = JSON.parse(atob(accessToken.split('.')[1])) as { exp?: number }
    if (!carga.exp) return true
    return carga.exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

/**
 * Renueva la sesion dejando que solo una pestaña lo haga.
 *
 * El servidor rota el token y trata como robo el que ya se uso. Sin coordinar,
 * dos pestañas que caducan a la vez se echan mutuamente de todas las sesiones.
 */
const renovarLaSesion = async (): Promise<string> => {
  const pedirloAlServidor = async (): Promise<string> => {
    const anterior = tokenStorage.getRefreshToken()
    if (!anterior) throw new Error('sin token de refresco')

    try {
      const respuesta = await axios.post(`${env.API_URL}/auth/refresh`, {
        refreshToken: anterior,
      })
      const { accessToken, refreshToken } = respuesta.data
      tokenStorage.setTokens(accessToken, refreshToken)
      return accessToken
    } catch (error) {
      // Otra pestaña renovo mientras tanto: su token ya esta guardado.
      const vigente = tokenStorage.getAccessToken()
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 409 &&
        vigente &&
        tokenStorage.getRefreshToken() !== anterior
      ) {
        return vigente
      }
      throw error
    }
  }

  if (typeof navigator === 'undefined' || !navigator.locks) {
    return pedirloAlServidor()
  }

  return navigator.locks.request('jh-lumina-renovar-sesion', async () => {
    const anterior = tokenStorage.getRefreshToken()
    const vigente = tokenStorage.getAccessToken()
    if (anterior && vigente && !estaCaducado(vigente)) return vigente
    return pedirloAlServidor()
  })
}

const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.API_URL,
    timeout: env.API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request Interceptor - Add Authorization header
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = tokenStorage.getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      darleTiempoSiLlevaArchivos(config)
      return config
    },
    (error: AxiosError) => {
      return Promise.reject(error)
    }
  )

  // Response Interceptor - Handle token refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      // If 401 and not already retrying
      if (error.response?.status === 401 && !originalRequest._retry) {
        // For login endpoint, just reject - let the UI handle the error
        if (originalRequest.url?.includes('/auth/login')) {
          return Promise.reject(error)
        }

        // For refresh endpoint, clear tokens and redirect to login
        if (originalRequest.url?.includes('/auth/refresh')) {
          tokenStorage.clearTokens()
          window.location.href = '/login'
          return Promise.reject(error)
        }

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              return client(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        const refreshToken = tokenStorage.getRefreshToken()
        if (!refreshToken) {
          tokenStorage.clearTokens()
          window.location.href = '/login'
          return Promise.reject(error)
        }

        try {
          const accessToken = await renovarLaSesion()

          processQueue(null, accessToken)

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`
          }

          return client(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError, null)
          tokenStorage.clearTokens()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    }
  )

  return client
}

// =============================================================================
// API Instance (Staff/Admin)
// =============================================================================

export const api = createApiClient()

// =============================================================================
// Patient API Client Factory (Completely separate)
// =============================================================================

const createPatientApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.API_URL,
    timeout: env.API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request Interceptor - Add Patient Authorization header
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = patientTokenStorage.getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      darleTiempoSiLlevaArchivos(config)
      return config
    },
    (error: AxiosError) => {
      return Promise.reject(error)
    }
  )

  // Response Interceptor - Handle 401 for patients (no refresh token)
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
      // If 401, clear patient tokens and redirect to patient login
      if (error.response?.status === 401) {
        // Don't redirect for login endpoint
        if (!error.config?.url?.includes('/auth/patient-login')) {
          patientTokenStorage.clearTokens()
          window.location.href = '/pacientes/login'
        }
      }
      return Promise.reject(error)
    }
  )

  return client
}

// =============================================================================
// Patient API Instance
// =============================================================================

export const patientApi = createPatientApiClient()

// =============================================================================
// Patient API Helper Functions
// =============================================================================

export async function patientGet<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await patientApi.get<T>(url, config)
  return response.data
}

export async function patientPost<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await patientApi.post<T>(url, data, config)
  return response.data
}

// =============================================================================
// Helper Functions (Staff/Admin)
// =============================================================================

/**
 * GET request helper
 */
export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.get<T>(url, config)
  return response.data
}

/**
 * POST request helper
 */
/** Para peticiones lentas por lo que tarda el servidor, no por su tamaño. */
export const PROCESO_LARGO: AxiosRequestConfig = { timeout: env.API_TIMEOUT_SUBIDA }

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<T>(url, data, config)
  return response.data
}

/**
 * PUT request helper
 */
export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.put<T>(url, data, config)
  return response.data
}

/**
 * PATCH request helper
 */
export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch<T>(url, data, config)
  return response.data
}

/**
 * DELETE request helper
 */
export async function del<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.delete<T>(url, config)
  return response.data
}

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data !== undefined
}
